import { Container } from '@dapperlabs/core-fe';
import { getCookie } from 'cookies-next';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'src/edge/session';
import Head from 'src/general/components/Head/Head';
import {
  flowAddressSansPrefix,
  isFlowAddress,
} from 'src/general/utils/flowAddress';
import { createApolloClient } from 'src/lib/apollo';
import { useTranslation } from 'src/lib/translations';
import {
  MomentsPageContent,
  MyCollectionHeader,
} from 'src/modules/MomentsPageContent';
import { getStaticMyCollectionPageContent } from 'src/modules/MomentsPageContent/staticContent';
import { UserProfileWithoutPii } from 'src/modules/Queries/generated';
import {
  searchUsersByFlowAddress,
  searchUsersByUsername,
  SEARCH_USERS,
} from 'src/modules/Queries/searchUsers';
import {
  SearchUsersQuery,
  SearchUsersQueryVariables,
} from 'src/modules/Queries/searchUsers.generated';
import { MessageComponent } from 'src/modules/Shared';
import { Box } from 'src/theme';

interface PageProps {
  locale: string;
  tabs?: any[];
  userProfile: UserProfileWithoutPii | null;
  username: string;
}

const PublicProfile = ({ locale, userProfile, tabs }: PageProps) => {
  const { t } = useTranslation();
  const { state: sessionState } = useSession();
  const router = useRouter();

  if (!userProfile) {
    return (
      <Box bg='linear-gradient(180deg, rgba(0, 36, 255, 0.7) 0%, #000000 128px)'>
        <MyCollectionHeader locale={locale} userProfile={null} />
        <Container maxW={{ md: '80%' }} px={{ base: 4, md: 0 }}>
          <MessageComponent
            CTA={t('MyCollection.Error.UsernameNotFound.CTA')}
            header={t('MyCollection.Error.UsernameNotFound.Header')}
            onClick={() => router.push('/')}
            subheader={t('MyCollection.Error.UsernameNotFound.Subheader')}
          />
        </Container>
      </Box>
    );
  }

  const isOwnProfile =
    sessionState.matches('AUTHENTICATED') &&
    sessionState.context.profile.username === userProfile.username;

  return (
    <>
      <Head
        subtitle={t('MyCollection.Head.UserCollectionSubtitle', {
          username: userProfile?.username,
        })}
      />
      <MomentsPageContent
        customTabs={tabs}
        isOwnProfile={isOwnProfile}
        locale={locale}
        userProfile={userProfile}
      />
    </>
  );
};

export default PublicProfile;

export const getServerSideProps = async ({
  req,
  res,
  params,
}: GetServerSidePropsContext) => {
  const locale = (getCookie('language', { req, res }) as string) || 'en-US';
  const client = createApolloClient();
  const content = getStaticMyCollectionPageContent(locale);
  const tabs = content.tabs;

  const username = params.username as string;
  const isParamFlowAddress = isFlowAddress(username);
  const flowAddress = isFlowAddress ? flowAddressSansPrefix(username) : null;

  const { data } = await client.query<
    SearchUsersQuery,
    SearchUsersQueryVariables
  >({
    context: {
      capturePolicy: 'none',
      clientName: 'laligaAPI',
      req,
    },
    query: SEARCH_USERS,
    variables: isParamFlowAddress
      ? searchUsersByFlowAddress(flowAddress)
      : searchUsersByUsername(username),
  });

  let userProfile: UserProfileWithoutPii = data?.searchUsers?.users[0] ?? null;
  if (isParamFlowAddress && !userProfile) {
    userProfile = {
      flowAddress,
      username,
    };
  }

  return {
    props: {
      locale,
      tabs,
      userProfile,
    },
  };
};
